import { Authenticated, Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import { FC } from "react";

import {
  AuthPage,
  ErrorComponent,
  Layout,
  ReadyPage,
  RefineSnackbarProvider,
  Title,
  notificationProvider,
} from "@refinedev/mui";

import {
  ArrowDropDownCircleOutlined,
  AssessmentOutlined,
  AttachMoney,
  PeopleOutline,
  PlusOneOutlined,
  ProductionQuantityLimitsOutlined,
  ProductionQuantityLimitsTwoTone,
  Receipt
} from "@mui/icons-material";
import { Box, Button, CssBaseline, GlobalStyles } from "@mui/material";
import routerBindings, {
  CatchAllNavigate,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { DataProvider } from "@refinedev/strapi-v4";
import { authProvider, axiosInstance } from "authProvider";
import DashboardComponent from "components/dashboard";
import { Header } from "components/header";
import { Sider } from "components/sider";
import { ColorModeContextProvider } from "contexts/color-mode";
import { ActionCreate } from "pages/actions/create";
import { ActionEdit } from "pages/actions/edit";
import { ActionList } from "pages/actions/list";
import { ActionShow } from "pages/actions/show";
import {
  BlogPostCreate,
  BlogPostEdit,
  BlogPostList,
  BlogPostShow,
} from "pages/blog-posts";
import { CasseCreate, CasseEdit, CasseList, CasseShow } from "pages/casse";
import {
  ClientCreate,
  ClientEdit,
  ClientList,
  ClientShow,
} from "pages/clients";
import { ContainerEdit } from "pages/containers/edit";
import { ContainerList } from "pages/containers/list";
import { ContainerShow } from "pages/containers/show";
import { CreditCreate } from "pages/credits/create";
import { CreditEdit } from "pages/credits/edit";
import { CreditList } from "pages/credits/list";
import { CreditShow } from "pages/credits/show";
import { DailyCreditsCreate } from "pages/credits_v2";
import { CreditListV2 } from "pages/credits_v2/list";
import {
  FarmerCreate,
  FarmerEdit,
  FarmerList,
  FarmerShow,
} from "pages/farmers";
import { ProductListList } from "pages/product_list";
import {
  PurshaseCreate,
  PurshaseEdit,
  PurshaseList,
  PurshaseShow,
} from "pages/purshase";
import {
  ReceiptCreate,
  ReceiptEdit,
  ReceiptList,
  ReceiptShow,
} from "pages/receipt";
import PaymentPage from "pages/receipt/pages/PaymentPage";
import { CreditsReport, ReceiptsReport, ReportingDashboard } from "pages/reporting";
import { useTranslation } from "react-i18next";
import { Outlet, Route, Routes, useNavigate } from "react-router-dom";
import { API_URL } from "../../constants";

const CustomDashboardPage = () => <DashboardComponent />;

export const RefinekbarContainer: FC = (): JSX.Element => {
  const { t, i18n } = useTranslation();

  const resources = [
    {
      name: "clients",
      list: "/clients",
      create: "/clients/create",
      edit: "/clients/edit/:id",
      show: "/clients/show/:id",
      meta: {
        label: t("clients"),
        icon: <PeopleOutline sx={{ color: "#023047" }} />,
        canDelete: true,
      },
    },

    {
      name: "containers",
      list: "/containers",
      create: "/containers/create",
      edit: "/containers/edit/:id",
      show: "/containers/show/:id",
      meta: {
        label: t("containers"),
        icon: <ProductionQuantityLimitsTwoTone sx={{ color: "#023047" }} />,
        canDelete: true,
      },
    },

    {
      name: "purshases",
      list: "/purshases",
      create: "/purshases/create",
      edit: "/purshases/edit/:id",
      show: "/purshases/show/:id",
      meta: {
        label: t("purshase"),
        icon: <PlusOneOutlined sx={{ color: "#023047" }} />,
        canDelete: true,
      },
    },
    {
      name: "casses",
      list: "/casses",
      create: "/casses/create",
      edit: "/casses/edit/:id",
      show: "/casses/show/:id",
      meta: {
        label: t("casse"),
        icon: <ArrowDropDownCircleOutlined sx={{ color: "#023047" }} />,
        canDelete: true,
      },
    },
    {
      name: "product-lists",
      list: "/product-lists",
      create: "/product-lists/create",
      edit: "/product-lists/edit/:id",
      show: "/product-lists/show/:id",
      meta: {
        label: t("product-list"),
        icon: <ProductionQuantityLimitsOutlined sx={{ color: "#023047" }} />,
        canDelete: true,
      },
    },
  
    {
      name: "credit-v2s",
      list: "/credit-v2s",
      create: "/credit-v2s/create",
      edit: "/credit-v2s/edit/:id",
      show: "/credit-v2s/show/:id",
      meta: {
        label: t("credits"),
        icon: <AttachMoney sx={{ color: "#023047" }} />,
        canDelete: true,
      },
    },
    {
      name: "receipts",
      list: "/receipts",
      create: "/receipts/create",
      edit: "/receipts/edit/:id",
      show: "/receipts/show/:id",
      meta: {
        label: t("receipts"),
        icon: <Receipt sx={{ color: "#023047" }} />,
        canDelete: true,
      },
    },
    {
      name: "reporting",
      list: "/reporting",
      meta: {
        label: "Reporting",
        icon: <AssessmentOutlined sx={{ color: "#023047" }} />,
        canDelete: false,
      },
    },
  ];

  const i18nProvider = {
    translate: (key: string, params: string) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  const navigate = useNavigate();

  return (
    <RefineKbarProvider>
      <ColorModeContextProvider>
        <CssBaseline />
        <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
        <RefineSnackbarProvider>
          <Refine
            i18nProvider={i18nProvider}
            authProvider={authProvider}
            dataProvider={DataProvider(API_URL + `/api`, axiosInstance)}
            notificationProvider={notificationProvider}
            routerProvider={routerBindings}
            Sider={Sider}
            resources={resources}
            DashboardPage={ReadyPage}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated
                    fallback={<CatchAllNavigate to="/login" />}
                    key="dashboard"
                  >
                    <Layout Header={Header} Title={Title} Sider={Sider}>
                      <Outlet />
                    </Layout>
                  </Authenticated>
                }
              >
                <Route path="/">
                  <Route index element={<CustomDashboardPage />} />
                </Route>
                <Route path="/blog-posts">
                  <Route index element={<BlogPostList />} />
                  <Route path="create" element={<BlogPostCreate />} />
                  <Route path="edit/:id" element={<BlogPostEdit />} />
                  <Route path="show/:id" element={<BlogPostShow />} />
                </Route>
                <Route path="/clients">
                  <Route index element={<ClientList />} />
                  <Route path="create" element={<ClientCreate />} />
                  <Route path="edit/:id" element={<ClientEdit />} />
                  <Route path="show/:id" element={<ClientShow />} />
                </Route>
                <Route path="/farmers">
                  <Route index element={<FarmerList />} />
                  <Route path="create" element={<FarmerCreate />} />
                  <Route path="edit/:id" element={<FarmerEdit />} />
                  <Route path="show/:id" element={<FarmerShow />} />
                </Route>
                <Route path="/containers">
                  <Route index element={<ContainerList />} />
                  {/* <Route path="create" element={<ContainerCreate />} /> */}
                  <Route path="edit/:id" element={<ContainerEdit />} />
                  <Route path="show/:id" element={<ContainerShow />} />
                </Route>
                <Route path="/actions">
                  <Route index element={<ActionList />} />
                  <Route path="create" element={<ActionCreate />} />
                  <Route path="edit/:id" element={<ActionEdit />} />
                  <Route path="show/:id" element={<ActionShow />} />
                </Route>
                <Route path="/purshases">
                  <Route index element={<PurshaseList />} />
                  <Route path="create" element={<PurshaseCreate />} />
                  <Route path="edit/:id" element={<PurshaseEdit />} />
                  <Route path="show/:id" element={<PurshaseShow />} />
                </Route>
                <Route path="/casses">
                  <Route index element={<CasseList />} />
                  <Route path="create" element={<CasseCreate />} />
                  <Route path="edit/:id" element={<CasseEdit />} />
                  <Route path="show/:id" element={<CasseShow />} />
                </Route>
                <Route path="/credits">
                  <Route index element={<CreditList />} />
                  <Route path="create" element={<CreditCreate />} />
                  <Route path="edit/:id" element={<CreditEdit />} />
                  <Route path="show/:id" element={<CreditShow />} />
                </Route>
                <Route path="/receipts">
                  <Route index element={<ReceiptList />} />
                  <Route path="create" element={<ReceiptCreate />} />
                  <Route path="edit/:id" element={<ReceiptEdit />} />
                  <Route path="show/:id" element={<ReceiptShow />} />
                  <Route path="payment" element={<PaymentPage />} />
                </Route>
                <Route path="/credit-v2s">
                  <Route index element={<CreditListV2 />} />
                  <Route path="create" element={<DailyCreditsCreate />} />
                  <Route path="edit/:id" element={<CreditEdit />} />
                  <Route path="show/:id" element={<CreditShow />} />
                </Route>
                <Route path="/product-lists">
                  <Route index element={<ProductListList />} />
                </Route>
                <Route path="/reporting">
                  <Route index element={<ReportingDashboard />} />
                  <Route path="receipts" element={<Box><Button onClick={() => navigate('/reporting')}>Go back</Button><ReceiptsReport /></Box>} />
                  <Route path="credits" element={<Box><Button onClick={() => navigate('/reporting')}>Go back</Button><CreditsReport /></Box>} />
                </Route>
              </Route>
              <Route
                element={
                  <Authenticated fallback={<Outlet />} key="NavigateToResource">
                    <NavigateToResource />
                  </Authenticated>
                }
              >
                <Route
                  path="/login"
                  element={
                    <AuthPage
                      type="login"
                      formProps={{
                        defaultValues: {
                          email: "demo@agrierp.com",
                          password: "Demo@AgriErp#",
                        },
                      }}
                    />
                  }
                />
              </Route>
              <Route
                element={
                  <Authenticated key="Layout">
                    <Layout Header={Header}>
                      <Outlet />
                    </Layout>
                  </Authenticated>
                }
              >
                <Route path="*" element={<ErrorComponent />} />
              </Route>
            </Routes>

            <RefineKbar />
            <UnsavedChangesNotifier />
          </Refine>
        </RefineSnackbarProvider>
      </ColorModeContextProvider>
    </RefineKbarProvider>
  );
};
